import { useState } from "react";
import {
  Alert,
  Button,
  Chip,
  ConfirmDialog,
  TextInput,
  ToggleInput,
} from "@a2z/ui";
import type { AppConfigType, AppConfigView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateAppConfigMutation } from "../services/useUpdateAppConfigMutation";
import { useResetAppConfigMutation } from "../services/useResetAppConfigMutation";
import { AppConfigAuditPanel } from "./AppConfigAuditPanel";

type Props = {
  config: AppConfigView;
};

type PendingAction = { kind: "save"; value: string } | { kind: "reset" } | null;

// Single-row editor for one app config. Layout priorities:
//  1. The value control (toggle / input) is the visual anchor on the right.
//  2. Localized name is the heading; technical key sits underneath in mono.
//  3. Description is small, gray, and optional-feeling.
//  4. Status chips (type, scope, source, updated) live in a single line
//     of small text rather than a row of colorful badges.
//  5. Save appears only when dirty. Reset appears only when there's a row.
//  6. Booleans + every reset go through ConfirmDialog — these flags
//     toggle real prod behavior.
export function AppConfigRow({ config }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const update = useUpdateAppConfigMutation();
  const reset = useResetAppConfigMutation();
  const initial = config.value ?? defaultFor(config);
  const [draft, setDraft] = useState<string>(initial);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction>(null);

  const isDirty = draft !== initial;
  const hasDbRow = config.source === "db";
  const isPending = update.isPending || reset.isPending;
  const name = locale === "ar" ? config.nameAr : config.nameEn;
  const description =
    locale === "ar" ? config.descriptionAr : config.descriptionEn;

  async function persist(value: string): Promise<void> {
    setError(null);
    try {
      await update.mutateAsync({ key: config.key, value });
    } catch (err) {
      setError(messageFor(err, t));
    }
  }

  async function performReset(): Promise<void> {
    setError(null);
    try {
      await reset.mutateAsync({ key: config.key });
      setDraft(defaultFor(config));
    } catch (err) {
      setError(messageFor(err, t));
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isDirty) return;
    if (config.type === "url" && !isValidUrl(draft)) {
      setError(t(keys.appConfigs.invalidUrl));
      return;
    }
    if (config.type === "boolean") {
      setPending({ kind: "save", value: draft });
      return;
    }
    await persist(draft);
  };

  return (
    <li className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)]">
      <form onSubmit={handleSubmit} className="px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">
                {name}
              </h3>
              <code className="font-mono text-[11px] text-[color:var(--text-muted)]">
                {config.key}
              </code>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-[color:var(--text-muted)]">
              {description}
            </p>
            <RowStatusLine config={config} t={t} locale={locale} />
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <RowControl
              config={config}
              value={draft}
              onChange={setDraft}
              disabled={isPending}
            />
          </div>
        </div>
        {error ? (
          <div className="mt-3">
            <Alert message={error} />
          </div>
        ) : null}
        <RowActions
          origin={config.origin}
          isDirty={isDirty}
          hasDbRow={hasDbRow}
          isPending={isPending}
          historyOpen={historyOpen}
          onToggleHistory={() => setHistoryOpen((v) => !v)}
          onResetClick={() => setPending({ kind: "reset" })}
          t={t}
        />
      </form>
      {historyOpen ? (
        <div className="border-t border-[color:var(--border-light)] px-4 py-3 sm:px-5">
          <AppConfigAuditPanel configKey={config.key} />
        </div>
      ) : null}
      <ConfirmDialog
        isOpen={pending?.kind === "save"}
        title={t(keys.appConfigs.confirmFlipTitle)}
        body={
          pending?.kind === "save" && pending.value === "true"
            ? t(keys.appConfigs.confirmFlipOn)
            : t(keys.appConfigs.confirmFlipOff)
        }
        confirmLabel={t(keys.appConfigs.confirmAction)}
        cancelLabel={t(keys.common.cancel)}
        intent="confirm"
        isSubmitting={isPending}
        onCancel={() => setPending(null)}
        onConfirm={async () => {
          if (pending?.kind === "save") {
            const value = pending.value;
            setPending(null);
            await persist(value);
          }
        }}
      />
      <ConfirmDialog
        isOpen={pending?.kind === "reset"}
        title={
          config.origin === "custom"
            ? t(keys.appConfigs.confirmDeleteTitle)
            : t(keys.appConfigs.confirmResetTitle)
        }
        body={
          config.origin === "custom"
            ? t(keys.appConfigs.confirmDeleteBody)
            : t(keys.appConfigs.confirmResetBody)
        }
        confirmLabel={
          config.origin === "custom"
            ? t(keys.appConfigs.confirmDeleteAction)
            : t(keys.appConfigs.confirmResetAction)
        }
        cancelLabel={t(keys.common.cancel)}
        intent="danger"
        isSubmitting={isPending}
        onCancel={() => setPending(null)}
        onConfirm={async () => {
          setPending(null);
          await performReset();
        }}
      />
    </li>
  );
}

function RowStatusLine({
  config,
  t,
  locale,
}: {
  config: AppConfigView;
  t: ReturnType<typeof useTranslations>;
  locale: string;
}) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <Chip tone="neutral" size="sm">
        {appConfigTypeLabel(config.type, t)}
      </Chip>
      <Chip tone={config.origin === "custom" ? "info" : "accent"} size="sm">
        {config.origin === "custom"
          ? t(keys.appConfigs.originCustom)
          : t(keys.appConfigs.originRegistry)}
      </Chip>
      <Chip tone={config.source === "db" ? "success" : "neutral"} size="sm">
        {config.source === "db"
          ? t(keys.appConfigs.sourceDb)
          : t(keys.appConfigs.sourceUnset)}
      </Chip>
      {config.scope === "boot" ? (
        <span title={t(keys.appConfigs.scopeBootHint)}>
          <Chip tone="danger" size="sm">
            {t(keys.appConfigs.scopeBoot)}
          </Chip>
        </span>
      ) : null}
      {config.updatedAt ? (
        <span className="ms-1 text-[11px] text-[color:var(--text-muted)]">
          {t(keys.appConfigs.updatedAt)}:{" "}
          {formatTimestamp(config.updatedAt, locale)}
        </span>
      ) : null}
    </div>
  );
}

function RowControl({
  config,
  value,
  onChange,
  disabled,
}: {
  config: AppConfigView;
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
}) {
  if (config.type === "boolean") {
    const checked = value.trim().toLowerCase() === "true";
    return (
      <ToggleInput
        name={config.key}
        ariaLabel={config.key}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked ? "true" : "false")}
      />
    );
  }
  // URLs get a wider field + native url keyboard; the inline validity check
  // runs on submit (see handleSubmit).
  const isUrl = config.type === "url";
  return (
    <div className={isUrl ? "w-full sm:w-72" : "w-40"}>
      <TextInput
        name={config.key}
        type={config.type === "integer" ? "number" : isUrl ? "url" : "text"}
        inputMode={config.type === "integer" ? "numeric" : undefined}
        value={value}
        min={config.min}
        max={config.max}
        hasError={isUrl && value.length > 0 && !isValidUrl(value)}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function RowActions({
  origin,
  isDirty,
  hasDbRow,
  isPending,
  historyOpen,
  onToggleHistory,
  onResetClick,
  t,
}: {
  origin: AppConfigView["origin"];
  isDirty: boolean;
  hasDbRow: boolean;
  isPending: boolean;
  historyOpen: boolean;
  onToggleHistory: () => void;
  onResetClick: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  const showSave = isDirty;
  const showReset = hasDbRow && !isDirty;
  if (!showSave && !showReset && !hasDbRow) {
    // Pristine + unset → only the history toggle.
    return (
      <div className="mt-3 flex justify-end">
        <HistoryToggle open={historyOpen} onClick={onToggleHistory} t={t} />
      </div>
    );
  }
  return (
    <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
      <HistoryToggle open={historyOpen} onClick={onToggleHistory} t={t} />
      {showReset ? (
        <Button
          type="button"
          variant="outline"
          onClick={onResetClick}
          disabled={isPending}
          className="h-8 rounded-md px-3 text-xs font-semibold max-lg:h-10"
        >
          {origin === "custom"
            ? t(keys.appConfigs.deleteAction)
            : t(keys.appConfigs.resetAction)}
        </Button>
      ) : null}
      {showSave ? (
        <Button
          type="submit"
          variant="primary"
          disabled={isPending}
          className="h-8 rounded-md px-3 text-xs font-semibold max-lg:h-10"
        >
          {isPending ? t(keys.appConfigs.saving) : t(keys.common.save)}
        </Button>
      ) : null}
    </div>
  );
}

function HistoryToggle({
  open,
  onClick,
  t,
}: {
  open: boolean;
  onClick: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs font-medium text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:underline max-lg:min-h-10"
    >
      {open ? t(keys.appConfigs.hideHistory) : t(keys.appConfigs.showHistory)}
    </button>
  );
}

const TYPE_LABEL_KEYS: Record<AppConfigType, string> = {
  boolean: keys.appConfigs.typeBoolean,
  integer: keys.appConfigs.typeInteger,
  string: keys.appConfigs.typeString,
  url: keys.appConfigs.typeUrl,
};

function appConfigTypeLabel(
  type: AppConfigType,
  t: ReturnType<typeof useTranslations>
): string {
  return t(TYPE_LABEL_KEYS[type]);
}

function defaultFor(config: AppConfigView): string {
  // The backend now sends the effective default (registry default for
  // registry keys), so an unset key shows its real value, not an empty field.
  if (config.defaultValue) return config.defaultValue;
  if (config.type === "boolean") return "false";
  if (config.type === "integer") return String(config.min ?? 0);
  return "";
}

// Accepts only absolute http(s) URLs — mirrors the backend's url-type
// validation so the seller sees the error before a round-trip.
function isValidUrl(value: string): boolean {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function formatTimestamp(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale);
}

function messageFor(
  err: unknown,
  t: ReturnType<typeof useTranslations>,
): string {
  if (err instanceof ApiJsonClientError) {
    return err.message || t(keys.errors.generic);
  }
  return t(keys.errors.generic);
}
